/**
 * Roles & Permissions Page
 *
 * Toggle-based permission management for admins. Left: role list (+ create).
 * Right: the selected role's permission matrix (module → section → action),
 * loaded from GET /api/rbac/ui/roles/{id}/matrix and saved as the full ON-set
 * via POST the same path (replace semantics, optimistic version).
 *
 * Permission `source` semantics: EXPLICIT / MODULE_FULL are editable;
 * INHERITED (from parent) and LOCKED_OFF (blocked by a DENY rule) are read-only.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldCheck,
  Plus,
  Search,
  Users,
  Trash2,
  RotateCcw,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Save,
  Info,
  AlertTriangle,
  CornerLeftUp,
  Lock,
  Layers,
  Pencil,
  X,
} from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Modal, ModalBody, ModalFooter, ConfirmationDialog } from '@/components/common/Modal';
import { Select } from '@/components/common/Select';
import { Badge } from '@/components/common/Badge';
import { Loader } from '@/components/common/Loader';
import { rbacService } from '@/api/services';
import type { RbacRole, RoleMatrix, MatrixModule, MatrixSection, MatrixPermission } from '@/api/types';
import { useToast, toast } from '@/hooks/useToast';
import { useCan } from '@/hooks/usePermissions';
import { cn } from '@/utils/cn';

type GroupState = 'full' | 'partial' | 'none';

// ============================================
// Display normalization (frontend-only overrides)
//
// The matrix tree — labels and module/section grouping — is backend-owned.
// These tweaks are presentation-only and safe because saving sends permission
// KEYS (see handleSave), not the tree shape:
//   • "Portal Users" → "Platform Users" (match the sidebar wording)
//   • fold sections that describe the SAME entity (module.entity) into one, so
//     the backend's separate "Registrations" section — whose key is
//     organization.organization.registration, the same entity as the main
//     Organization section — shows as a registration toggle inside Organization
//     rather than a section of its own.
// Keyed off permission-key prefixes so a backend label change won't break them.
// ============================================

const modulePrefix = (m: MatrixModule): string | undefined =>
  m.sections[0]?.permissions[0]?.key.split('.')[0];

/** `module.entity` prefix of a section (first two key segments). */
const sectionEntity = (s: MatrixSection): string =>
  s.permissions[0]?.key.split('.').slice(0, 2).join('.') ?? s.key;

/** Merge sections sharing an entity into one, preserving first-seen order. */
function mergeSectionsByEntity(sections: MatrixSection[]): MatrixSection[] {
  const groups = new Map<string, MatrixSection[]>();
  const order: string[] = [];
  for (const s of sections) {
    const entity = sectionEntity(s);
    if (!groups.has(entity)) {
      groups.set(entity, []);
      order.push(entity);
    }
    groups.get(entity)!.push(s);
  }
  return order.map((entity) => {
    const grp = groups.get(entity)!;
    if (grp.length === 1) return grp[0];
    // Keep the section carrying the base `.view` action as the label/description
    // anchor; append the others' permissions (deduped, view/manage first).
    const primary = grp.find((s) => s.permissions.some((p) => p.key.endsWith('.view'))) ?? grp[0];
    const seen = new Set<string>();
    const permissions = [primary, ...grp.filter((s) => s !== primary)]
      .flatMap((s) => s.permissions)
      .filter((p) => (seen.has(p.key) ? false : (seen.add(p.key), true)));
    return { ...primary, permissions };
  });
}

function normalizeMatrixTree(tree: MatrixModule[]): MatrixModule[] {
  return tree.map((m) => ({
    ...m,
    label:
      modulePrefix(m) === 'orgUser' || m.label.trim().toLowerCase() === 'portal users'
        ? 'Platform Users'
        : m.label,
    sections: mergeSectionsByEntity(m.sections),
  }));
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

// ============================================
// Tri-state switch (supports indeterminate)
// ============================================

interface SwitchProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange?: (on: boolean) => void;
  label?: string;
}

function Switch({ checked, indeterminate, disabled, onChange, label }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange?.(!checked);
      }}
      className={cn(
        'relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 focus-visible:ring-offset-card dark:focus-visible:ring-offset-card-dark',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        indeterminate ? 'bg-warning' : checked ? 'bg-primary' : 'bg-foreground-muted/25 dark:bg-foreground-dark-muted/25'
      )}
    >
      <span
        className={cn(
          'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200',
          indeterminate ? 'translate-x-[11px]' : checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
        )}
      />
    </button>
  );
}

function StateChip({ state }: { state: GroupState }) {
  return (
    <Badge variant={state === 'full' ? 'success' : state === 'partial' ? 'warning' : 'default'} size="sm">
      {state === 'full' ? 'Full access' : state === 'partial' ? 'Partial' : 'No access'}
    </Badge>
  );
}

// ============================================
// Page
// ============================================

export function RolesPermissionsPage() {
  // Stable module-level `toast` — the useToast() hook returns a fresh object
  // each render and would loop the memoized fetchers.
  const canManage = useCan('userMgmt.role.manage');

  const [roles, setRoles] = useState<RbacRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<RoleMatrix | null>(null);
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [original, setOriginal] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState<RbacRole | null>(null);
  const [deletingRole, setDeletingRole] = useState<RbacRole | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // ----- data loading -----

  const fetchRoles = useCallback(async (): Promise<RbacRole[]> => {
    try {
      const response = await rbacService.getRoles();
      if (response.statusCode === 200 && response.result) {
        setRoles(response.result);
        return response.result;
      }
      toast.error(response.statusMessage || 'Failed to load roles');
    } catch (error) {
      console.error('Error fetching roles:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        toast.error('Failed to load roles');
      }
    }
    return [];
  }, []);

  const fetchMatrix = useCallback(async (roleId: string) => {
    if (!roleId) return;
    try {
      setIsLoading(true);
      const response = await rbacService.getRoleMatrix(roleId);
      if (response.statusCode === 200 && response.result) {
        const result: RoleMatrix = {
          ...response.result,
          tree: normalizeMatrixTree(response.result.tree),
        };
        const draft: Record<string, boolean> = {};
        result.tree.forEach((m) =>
          m.sections.forEach((s) =>
            s.permissions.forEach((p) => {
              draft[p.key] = !!p.enabled;
            })
          )
        );
        setMatrix(result);
        setEnabled(draft);
        setOriginal({ ...draft });
        setOpenModules({});
      } else {
        toast.error(response.statusMessage || 'Failed to load permissions');
      }
    } catch (error) {
      console.error('Error fetching matrix:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const list = await fetchRoles();
      if (list.length) setSelectedRoleId(list[0].id);
      else setIsLoading(false);
    })();
  }, [fetchRoles]);

  useEffect(() => {
    if (selectedRoleId) void fetchMatrix(selectedRoleId);
  }, [selectedRoleId, fetchMatrix]);

  // ----- draft helpers -----

  const changeCount = useMemo(
    () => Object.keys(enabled).filter((k) => enabled[k] !== original[k]).length,
    [enabled, original]
  );

  // Live "permissions enabled" summary for the selected role.
  const permStats = useMemo(() => {
    if (!matrix) return { on: 0, total: 0 };
    let on = 0;
    let total = 0;
    matrix.tree.forEach((m) =>
      m.sections.forEach((s) =>
        s.permissions.forEach((p) => {
          total += 1;
          if (enabled[p.key]) on += 1;
        })
      )
    );
    return { on, total };
  }, [matrix, enabled]);

  const isEditable = (p: MatrixPermission) => p.source !== 'INHERITED' && p.source !== 'LOCKED_OFF';

  const groupState = (perms: MatrixPermission[]): GroupState => {
    const on = perms.filter((p) => enabled[p.key]).length;
    if (on === 0) return 'none';
    return on === perms.length ? 'full' : 'partial';
  };

  const setPerm = (
    draft: Record<string, boolean>,
    section: MatrixSection,
    p: MatrixPermission,
    on: boolean
  ) => {
    if (!isEditable(p)) return;
    draft[p.key] = on;
    const viewPerm = section.permissions.find((x) => x.key.endsWith('.view'));
    if (!viewPerm) return;
    // "can do" implies "can see"
    if (on && p.key !== viewPerm.key && isEditable(viewPerm)) draft[viewPerm.key] = true;
    if (!on && p.key === viewPerm.key) {
      section.permissions.forEach((x) => {
        if (isEditable(x)) draft[x.key] = false;
      });
    }
  };

  const togglePerm = (section: MatrixSection, p: MatrixPermission, on: boolean) =>
    setEnabled((prev) => {
      const draft = { ...prev };
      setPerm(draft, section, p, on);
      return draft;
    });

  const toggleSection = (section: MatrixSection, on: boolean) =>
    setEnabled((prev) => {
      const draft = { ...prev };
      section.permissions.forEach((p) => {
        if (isEditable(p)) draft[p.key] = on;
      });
      return draft;
    });

  const toggleModule = (module: MatrixModule, on: boolean) =>
    setEnabled((prev) => {
      const draft = { ...prev };
      module.sections.forEach((s) =>
        s.permissions.forEach((p) => {
          if (isEditable(p)) draft[p.key] = on;
        })
      );
      return draft;
    });

  // ----- actions -----

  const handleSelectRole = (id: string) => {
    if (id === selectedRoleId) return;
    if (changeCount > 0 && !window.confirm('Discard unsaved changes?')) return;
    setSelectedRoleId(id);
  };

  const handleSave = async () => {
    if (!selectedRoleId || !matrix) return;
    try {
      setIsSaving(true);
      const enabledKeys = Object.keys(enabled).filter((k) => enabled[k]);
      const response = await rbacService.saveRoleMatrix(selectedRoleId, {
        version: matrix.role.version,
        enabledKeys,
      });
      if (response.statusCode === 200) {
        toast.success('Permissions updated successfully.');
        await fetchRoles();
        await fetchMatrix(selectedRoleId);
      } else {
        toast.error(response.statusMessage || 'Failed to save permissions');
      }
    } catch (error) {
      console.error('Error saving matrix:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        toast.error('Failed to save permissions');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => setEnabled({ ...original });

  const confirmReset = async () => {
    if (!selectedRoleId) return;
    try {
      setIsResetting(true);
      const response = await rbacService.resetSystemRole(selectedRoleId);
      if (response.statusCode === 200) {
        toast.success('Role reset to its default permissions.');
        await fetchMatrix(selectedRoleId);
      } else {
        toast.error(response.statusMessage || 'Failed to reset role');
      }
    } catch (error) {
      console.error('Error resetting role:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        toast.error('Failed to reset role');
      }
    } finally {
      setIsResetting(false);
      setShowResetDialog(false);
    }
  };

  // ----- search filter -----

  const filteredTree = useMemo<MatrixModule[]>(() => {
    if (!matrix) return [];
    const q = search.trim().toLowerCase();
    if (!q) return matrix.tree;
    return matrix.tree
      .map((m) => ({
        ...m,
        sections: m.sections
          .map((s) => ({
            ...s,
            permissions: s.permissions.filter((p) =>
              `${m.label} ${s.label} ${p.label} ${p.description ?? ''}`.toLowerCase().includes(q)
            ),
          }))
          .filter((s) => s.permissions.length),
      }))
      .filter((m) => m.sections.length);
  }, [matrix, search]);

  const allExpanded = filteredTree.length > 0 && filteredTree.every((m) => openModules[m.key]);

  const toggleAllModules = () => {
    const next: Record<string, boolean> = {};
    if (!allExpanded) filteredTree.forEach((m) => { next[m.key] = true; });
    setOpenModules(next);
  };

  const role = matrix?.role;
  const progress = permStats.total ? Math.round((permStats.on / permStats.total) * 100) : 0;

  // ----- render -----

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-600 text-white shadow-sm">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-foreground dark:text-foreground-dark">
            Roles &amp; Permissions
          </h1>
          <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Control what each role can see and do. Changes apply to everyone holding the role.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Role list */}
        <aside className="lg:w-72 flex-none">
          <div className="mb-2 flex items-center justify-between px-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground-muted dark:text-foreground-dark-muted">
              Roles {roles.length ? `(${roles.length})` : ''}
            </span>
          </div>

          {canManage && (
            <Button
              className="mb-3 w-full justify-center"
              onClick={() => { setEditingRole(null); setShowRoleForm(true); }}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              New role
            </Button>
          )}

          <div className="space-y-1.5">
            {roles.map((r) => {
              const active = r.id === selectedRoleId;
              return (
                <button
                  key={r.id}
                  onClick={() => handleSelectRole(r.id)}
                  className={cn(
                    'group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-200',
                    active
                      ? 'border-primary/60 bg-primary-50 shadow-sm ring-1 ring-primary/30 dark:bg-primary-900/20'
                      : 'border-border bg-card hover:border-primary/30 hover:bg-secondary-50 dark:border-border-dark dark:bg-card-dark dark:hover:bg-secondary-800/40'
                  )}
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 flex-none items-center justify-center rounded-lg text-xs font-bold transition-colors',
                      active
                        ? 'bg-primary text-white'
                        : 'bg-secondary-100 text-foreground-muted group-hover:bg-primary-100 group-hover:text-primary dark:bg-secondary-800 dark:text-foreground-dark-muted'
                    )}
                  >
                    {initials(r.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground dark:text-foreground-dark">
                      {r.name}
                    </span>
                    <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-muted dark:text-foreground-dark-muted">
                      <Badge variant={r.isSystem ? 'info' : 'default'} size="sm">
                        {r.isSystem ? 'System' : 'Custom'}
                      </Badge>
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {r.assigneeCount ?? 0}
                      </span>
                    </span>
                  </span>
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 flex-none transition-colors',
                      active ? 'text-primary' : 'text-transparent group-hover:text-foreground-muted'
                    )}
                  />
                </button>
              );
            })}
            {!roles.length && !isLoading && (
              <p className="rounded-xl border border-dashed border-border px-3 py-8 text-center text-sm text-foreground-muted dark:border-border-dark">
                No roles yet — create one to get started.
              </p>
            )}
          </div>
        </aside>

        {/* Matrix */}
        <main className="min-w-0 flex-1">
          {isLoading ? (
            <div className="rounded-2xl border border-border bg-card py-24 dark:border-border-dark dark:bg-card-dark">
              <Loader message="Loading permissions…" />
            </div>
          ) : !role ? (
            <div className="rounded-2xl border border-dashed border-border py-24 text-center text-foreground-muted dark:border-border-dark">
              Select a role to edit its permissions.
            </div>
          ) : (
            <>
              {/* Role header card */}
              <div className="mb-4 rounded-2xl border border-border bg-card p-5 dark:border-border-dark dark:bg-card-dark">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5">
                      <h2 className="truncate text-lg font-bold text-foreground dark:text-foreground-dark">
                        {role.name}
                      </h2>
                      <Badge variant={role.isSystem ? 'info' : 'default'} size="sm">
                        {role.isSystem ? 'System' : 'Custom'}
                      </Badge>
                    </div>
                    {role.description && (
                      <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
                        {role.description}
                      </p>
                    )}
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-muted">
                      <Users className="h-3.5 w-3.5" />
                      {role.assigneeCount ?? 0} user{(role.assigneeCount ?? 0) === 1 ? '' : 's'} affected
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setEditingRole(role); setShowRoleForm(true); }}
                        leftIcon={<Pencil className="h-3.5 w-3.5" />}
                      >
                        Edit
                      </Button>
                      {role.isSystem ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowResetDialog(true)}
                          leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                        >
                          Reset
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-danger hover:bg-danger-50 dark:hover:bg-danger-900/20"
                          onClick={() => setDeletingRole(role)}
                          aria-label="Delete role"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Permissions-enabled meter */}
                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground-muted dark:text-foreground-dark-muted">
                      Permissions enabled
                    </span>
                    <span className="font-semibold text-foreground dark:text-foreground-dark">
                      {permStats.on} / {permStats.total}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary-100 dark:bg-secondary-800">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Banners */}
              {matrix.hasParent && (
                <div className="mb-3 flex items-start gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-sm text-primary-700 dark:bg-primary-900/20 dark:text-primary-300">
                  <Info className="mt-0.5 h-4 w-4 flex-none" />
                  <span>
                    This role inherits from a parent role. Inherited permissions are marked and can only
                    be changed on the parent.
                  </span>
                </div>
              )}
              {matrix.hasAdvancedRules && (
                <div className="mb-3 flex items-start gap-2 rounded-xl bg-warning-50 px-3.5 py-2.5 text-sm text-warning-700 dark:bg-warning/10 dark:text-warning">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                  <span>
                    This role has advanced rules configured by support. Blocked items stay off; saving
                    this screen replaces those rules.
                  </span>
                </div>
              )}

              {/* Toolbar: search + expand toggle */}
              <div className="mb-4 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search permissions… (e.g. plan, manage, audit)"
                    className="pl-9"
                  />
                </div>
                {filteredTree.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleAllModules}
                    leftIcon={
                      allExpanded ? (
                        <ChevronsDownUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronsUpDown className="h-3.5 w-3.5" />
                      )
                    }
                    className="flex-none"
                  >
                    <span className="hidden sm:inline">{allExpanded ? 'Collapse all' : 'Expand all'}</span>
                  </Button>
                )}
              </div>

              {/* Module cards */}
              <div className="space-y-3">
                {filteredTree.map((module) => {
                  const allPerms = module.sections.flatMap((s) => s.permissions);
                  const state = groupState(allPerms);
                  const isOpen = search ? true : !!openModules[module.key];
                  return (
                    <div
                      key={module.key}
                      className={cn(
                        'overflow-hidden rounded-2xl border bg-card transition-colors dark:bg-card-dark',
                        isOpen
                          ? 'border-primary/30 dark:border-primary/30'
                          : 'border-border dark:border-border-dark'
                      )}
                    >
                      {/* module head */}
                      <div
                        className="flex cursor-pointer select-none items-center gap-3 px-4 py-3.5 transition-colors hover:bg-secondary-50 dark:hover:bg-secondary-800/30"
                        onClick={() => setOpenModules((prev) => ({ ...prev, [module.key]: !isOpen }))}
                      >
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 flex-none text-foreground-muted transition-transform duration-200',
                            isOpen && 'rotate-90'
                          )}
                        />
                        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary dark:bg-primary-900/30">
                          <Layers className="h-4 w-4" />
                        </span>
                        <Switch
                          checked={state === 'full'}
                          indeterminate={state === 'partial'}
                          disabled={!canManage}
                          onChange={(on) => toggleModule(module, on)}
                          label={`Toggle all ${module.label}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-foreground dark:text-foreground-dark">
                            {module.label}
                          </div>
                          {module.description && (
                            <div className="truncate text-xs text-foreground-muted">
                              {module.description}
                            </div>
                          )}
                        </div>
                        <StateChip state={state} />
                      </div>

                      {/* sections */}
                      {isOpen && (
                        <div className="border-t border-border dark:border-border-dark">
                          {module.sections.map((section) => {
                            const sState = groupState(section.permissions);
                            return (
                              <div
                                key={section.key}
                                className="border-b border-border/60 px-4 py-3.5 last:border-b-0 dark:border-border-dark/60"
                              >
                                <div className="flex items-center gap-3 md:pl-7">
                                  <Switch
                                    checked={sState === 'full'}
                                    indeterminate={sState === 'partial'}
                                    disabled={!canManage}
                                    onChange={(on) => toggleSection(section, on)}
                                    label={`Toggle all ${section.label}`}
                                  />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-foreground dark:text-foreground-dark">
                                      {section.label}
                                    </div>
                                    {section.description && (
                                      <div className="text-xs text-foreground-muted">
                                        {section.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 md:pl-16 xl:grid-cols-3">
                                  {section.permissions.map((p) => {
                                    const editable = isEditable(p);
                                    return (
                                      <div
                                        key={p.key}
                                        className={cn(
                                          'flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors',
                                          canManage && editable && 'hover:bg-secondary-50 dark:hover:bg-secondary-800/30'
                                        )}
                                      >
                                        <Switch
                                          checked={!!enabled[p.key]}
                                          disabled={!canManage || !editable}
                                          onChange={(on) => togglePerm(section, p, on)}
                                          label={p.label}
                                        />
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-1.5 text-sm font-medium text-foreground dark:text-foreground-dark">
                                            <span className={!editable ? 'text-foreground-muted' : ''}>
                                              {p.label}
                                            </span>
                                            {p.source === 'INHERITED' && (
                                              <span
                                                title="Granted by the parent role — change it there"
                                                className="flex items-center gap-0.5 rounded bg-primary-100 px-1.5 py-px text-[10px] font-bold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                                              >
                                                <CornerLeftUp className="h-2.5 w-2.5" />
                                                INHERITED
                                              </span>
                                            )}
                                            {p.source === 'LOCKED_OFF' && (
                                              <span
                                                title="Blocked by an advanced rule"
                                                className="flex items-center gap-0.5 rounded bg-danger-50 px-1.5 py-px text-[10px] font-bold text-danger-700 dark:bg-danger/20 dark:text-danger"
                                              >
                                                <Lock className="h-2.5 w-2.5" />
                                                BLOCKED
                                              </span>
                                            )}
                                          </div>
                                          {p.description && (
                                            <div
                                              className="truncate text-[11px] text-foreground-muted"
                                              title={p.description}
                                            >
                                              {p.description}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!filteredTree.length && (
                  <div className="rounded-2xl border border-dashed border-border py-16 text-center text-foreground-muted dark:border-border-dark">
                    {matrix.tree.length ? 'Nothing matches your search.' : 'No permissions available.'}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Floating save pill */}
      {canManage && changeCount > 0 && (
        <div className="fixed inset-x-0 bottom-5 z-30 flex justify-center px-4">
          <div className="flex items-center gap-3 rounded-full border border-border bg-card/95 py-2 pl-4 pr-2 shadow-xl backdrop-blur dark:border-border-dark dark:bg-card-dark/95">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-warning/15 text-xs font-bold text-warning">
              {changeCount}
            </span>
            <span className="text-sm font-medium text-foreground dark:text-foreground-dark">
              unsaved change{changeCount === 1 ? '' : 's'}
            </span>
            <div className="mx-1 h-5 w-px bg-border dark:bg-border-dark" />
            <Button variant="ghost" size="sm" onClick={handleDiscard} disabled={isSaving}>
              Discard
            </Button>
            <Button size="sm" onClick={handleSave} loading={isSaving} leftIcon={<Save className="h-4 w-4" />}>
              Save changes
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <RoleFormModal
        open={showRoleForm}
        role={editingRole}
        onClose={() => setShowRoleForm(false)}
        onSaved={async (roleId) => {
          setShowRoleForm(false);
          await fetchRoles();
          if (roleId) setSelectedRoleId(roleId);
        }}
      />
      <DeleteRoleModal
        role={deletingRole}
        roles={roles}
        onClose={() => setDeletingRole(null)}
        onDeleted={async () => {
          setDeletingRole(null);
          const list = await fetchRoles();
          setSelectedRoleId(list[0]?.id ?? null);
        }}
      />

      {/* Reset-to-default confirmation */}
      <ConfirmationDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        title="Reset role to defaults?"
        message={`This restores "${role?.name ?? 'this role'}" to its original system permissions and removes any customizations. This can't be undone.`}
        confirmText="Reset to default"
        cancelText="Cancel"
        variant="warning"
        onConfirm={confirmReset}
        isLoading={isResetting}
      />
    </div>
  );
}

// ============================================
// Role create / edit modal
// ============================================

interface RoleFormModalProps {
  open: boolean;
  role: RbacRole | null;
  onClose: () => void;
  onSaved: (roleId?: string) => void;
}

function RoleFormModal({ open, role, onClose, onSaved }: RoleFormModalProps) {
  const t = useToast();
  const isEdit = !!role;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(role?.name ?? '');
      setCode(role?.code ?? '');
      setDescription(role?.description ?? '');
    }
  }, [open, role]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEdit) {
      setCode(
        value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64)
      );
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isEdit && role) {
        const response = await rbacService.updateRole({
          roleId: role.id,
          name: name.trim(),
          description: description.trim(),
          parentRoleId: role.parentRoleId ?? null,
          status: role.status,
          version: role.version,
        });
        if (response.statusCode === 200) {
          t.success('Role updated successfully.');
          onSaved(role.id);
        } else {
          t.error(response.statusMessage || 'Could not update the role');
        }
      } else {
        const response = await rbacService.createRole({
          code,
          name: name.trim(),
          description: description.trim(),
          parentRoleId: null,
        });
        if (response.statusCode === 200 && response.result?.roleId) {
          t.success('Role created successfully — now choose what it can do.');
          onSaved(response.result.roleId);
        } else {
          t.error(response.statusMessage || 'Could not create the role');
        }
      }
    } catch (error) {
      console.error('Error saving role:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        t.error('Could not save the role');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? 'Edit role' : 'Create a new role'}
      description={isEdit ? 'Update this role’s name and description.' : 'Name the role, then choose its permissions.'}
      size="md"
    >
      <ModalBody className="space-y-4">
        <Input
          label="Name"
          required
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g. Branch Manager"
          helperText="Shown to your team when assigning roles."
        />
        <Input
          label="Code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. BRANCH_MANAGER"
          disabled={isEdit}
          className="font-mono"
          helperText={isEdit ? 'The code cannot be changed after creation.' : 'A short, unique identifier.'}
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this role for?"
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={submitting} disabled={!name.trim() || !code}>
          {isEdit ? 'Save changes' : 'Create role'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================
// Delete role modal (with reassignment)
// ============================================

interface DeleteRoleModalProps {
  role: RbacRole | null;
  roles: RbacRole[];
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteRoleModal({ role, roles, onClose, onDeleted }: DeleteRoleModalProps) {
  const t = useToast();
  const [reassignToRoleId, setReassignToRoleId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const hasAssignees = (role?.assigneeCount ?? 0) > 0;

  useEffect(() => {
    if (role) setReassignToRoleId('');
  }, [role]);

  const handleDelete = async () => {
    if (!role) return;
    setSubmitting(true);
    try {
      const response = await rbacService.deleteRole({
        roleId: role.id,
        reassignToRoleId: hasAssignees ? reassignToRoleId : undefined,
      });
      if (response.statusCode === 200) {
        t.success('Role deleted successfully.');
        onDeleted();
      } else {
        t.error(response.statusMessage || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      if (!(error as { isPermissionDenied?: boolean })?.isPermissionDenied) {
        t.error('Failed to delete role');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reassignOptions = roles
    .filter((r) => r.id !== role?.id)
    .map((r) => ({ value: r.id, label: r.name }));

  return (
    <Modal open={!!role} onOpenChange={(o) => !o && onClose()} title={`Delete “${role?.name ?? ''}”`} size="md">
      <ModalBody className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl bg-danger-50 p-3.5 dark:bg-danger-900/20">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-danger/15 text-danger">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <p className="text-sm text-danger-700 dark:text-danger">
            This permanently removes the role and can’t be undone.
            {hasAssignees
              ? ` ${role?.assigneeCount} user${(role?.assigneeCount ?? 0) === 1 ? '' : 's'} currently hold it — pick a role to move them to.`
              : ' No users currently hold it.'}
          </p>
        </div>
        {hasAssignees && (
          <Select
            label="Reassign users to"
            required
            options={reassignOptions}
            value={reassignToRoleId}
            onChange={setReassignToRoleId}
            placeholder="Select a role…"
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} leftIcon={<X className="h-4 w-4" />}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          loading={submitting}
          disabled={hasAssignees && !reassignToRoleId}
          leftIcon={<Trash2 className="h-4 w-4" />}
        >
          Delete role
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default RolesPermissionsPage;
